import Select from 'react-select';
import EventBus from 'eventing-bus';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import { bulkUpdateObjects } from '../../store/Objects';

const BulkAssignModal = ({ isOpen, onClose, objectSchema, selectedIds, onRefresh, onClearSelection, allRecordsSelected = false, totalCount = 0, viewId = null, filters = null }) => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // When "Select all N records" mode is active, assign applies to every record matching the current view/filters
  const effectiveCount = allRecordsSelected ? totalCount : selectedIds.length;

  // Get members from currentWorkspace
  const members = currentWorkspace?.['members'] || [];
  
  // Format members for react-select
  const memberOptions = [
    { value: null, label: 'No owner' },
    ...members.map(member => ({
      value: member['_id'],
      label: `${member['firstName'] || ''} ${member['lastName'] || ''}`.trim() || member['email'],
      email: member['email']
    }))
  ];

  const handleUpdate = () => {
    if (!objectSchema) return
    
    setIsUpdating(true)
    dispatch(bulkUpdateObjects({
      objectType: objectSchema['type'],
      ids: selectedIds,
      updates: { ownerId: selectedOwner },
      updateAll: allRecordsSelected || undefined,
      viewId: allRecordsSelected ? viewId : undefined,
      filters: allRecordsSelected ? filters : undefined,
      successCallback: () => {
        EventBus.publish('success', `Successfully assigned ${effectiveCount} ${objectSchema['label'].toLowerCase()}${effectiveCount > 1 ? 's' : ''}`)
        setIsUpdating(false)
        // Only clear selection on successful update
        setSelectedOwner(null)
        onClose()
        onClearSelection()
        if (onRefresh) onRefresh()
      },
      failCallback: (error) => {
        EventBus.publish('error', error?.message || 'Failed to assign records')
        setIsUpdating(false)
      }
    }))
  }

  const handleClose = () => {
    // Reset form state but don't clear selection on cancel
    setSelectedOwner(null)
    onClose()
    // Commented out: onClearSelection() - Don't clear selection on cancel, only on success
    // onClearSelection()
  }

  // Custom option rendering to show email
  const formatOptionLabel = ({ label, email }) => (
    <div>
      <div>{label}</div>
      {email && <div style={{ fontSize: '12px', color: '#888' }}>{email}</div>}
    </div>
  )

  return (
    <Modal isOpen={isOpen} toggle={handleClose} keyboard={true} className="main-modal modal-dialog-centered new-user-modal bulkedit-modal">
      <ModalHeader toggle={handleClose}></ModalHeader>
      <ModalBody>
        <div className="content-area">
          <h4> Bulk assign {effectiveCount} {objectSchema?.['label'].toLowerCase() || 'record'}{effectiveCount > 1 ? 's' : ''}</h4>
          <div className="form-area">
            <div className="form-group">
              <label className="form-label">{objectSchema?.['label'] || 'Record'} owner</label>
              <Select
                value={memberOptions.find(opt => opt['value'] === selectedOwner)}
                onChange={(option) => setSelectedOwner(option?.['value'])}
                options={memberOptions}
                placeholder="Select owner"
                isClearable={false}
                isSearchable
                className="react-select-container"
                classNamePrefix="react-select"
                formatOptionLabel={formatOptionLabel}
                styles={{
                  control: (base) => ({ 
                    ...base, 
                    backgroundColor: '#fff', 
                    borderColor: '#00BFA5',
                    minHeight: '48px',
                    '&:hover': { borderColor: '#00A693' }
                  }),
                  menu: (base) => ({ 
                    ...base, 
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0'
                  }),
                  option: (base, state) => ({ 
                    ...base, 
                    backgroundColor: state['isFocused'] ? '#f5f5f5' : '#fff',
                    color: '#333',
                    '&:active': { backgroundColor: '#e0e0e0' }
                  }),
                  singleValue: (base) => ({ ...base, color: '#333' }),
                  placeholder: (base) => ({ ...base, color: '#999' }),
                  input: (base) => ({ ...base, color: '#333' })
                }}
              />
            </div>

            <div className="btns-box text-right mt-4">
              <button 
                className="btn-style-border mr-3" 
                onClick={handleClose}
                disabled={isUpdating}
                style={{ marginRight: '10px' }}
              >
                Cancel
              </button>
              <button
                className="btn-style-new"
                onClick={handleUpdate}
                disabled={isUpdating}
                style={{ 
                  opacity: isUpdating ? 0.7 : 1
                }}
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default BulkAssignModal;